import React from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import CustomCreatableDropdown from "custom-components/shared/CustomCreatableDropdown";
import { MultiSelectCheckboxDropdown } from "custom-components/shared/MultiSelectCheckboxDropdown";
import { DatePicker } from "components/shared/form/Datepicker";
import { TimePicker } from "components/shared/form/Timepicker";
import RichTextEditor from "components/shared/RichTextEditor/RichTextEditor";
import { AttachmentsActions } from "components/shared/AttachmentsActions";
import { EntityDynamicFieldsForm } from "components/shared/dynamic-fields";
import { DocumentKeyEnum } from "enum/DocumentKeyEnum";
import { EntityTypeEnum } from "enum/EntityTypeEnum";
import { Field, INPUT_CLS, LABEL_CLS } from "../shared/WhsFormField";
import { stripHtml } from "../shared/whsHelpers";

export default function ToolboxTalkFormFields({
  presenter,
  setPresenter,
  internalUsers,
  status,
  setStatus,
  statusOptions,
  loadingStatus,
  handleCreateStatus,
  handleEditStatus,
  handleDeleteStatus,
  talkDate,
  setTalkDate,
  time,
  setTime,
  topic,
  setTopic,
  discussionPoints,
  setDiscussionPoints,
  users,
  selectedWorkers,
  setSelectedWorkers,
  loadingJobUsers,
  docFiles,
  setDocFiles,
  setPreviewDoc,
  setIsPreviewOpen,
  notes,
  setNotes,
  dynamicFieldsRef,
  companyId,
  userId,
  loggedInUser,
  errors = {},
  setErrors,
  isSubmitting,
  onSubmit,
  onCancel,
}) {
  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4 font-sans">
      <div className="space-y-3.5">
        <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
          {/* Conducted By */}
          <Field
            id="field-presenter"
            label="Conducted By"
            required
            error={errors.presenter}
          >
            <MultiSelectCheckboxDropdown
              required
              value={presenter}
              onChange={(val) => {
                setPresenter(val || "");
                if (errors.presenter && setErrors) {
                  setErrors((prev) => ({ ...prev, presenter: "" }));
                }
              }}
              options={internalUsers}
              placeholder="Select supervisor..."
              isMulti={false}
              usePortal={false}
              error={Boolean(errors.presenter)}
            />
          </Field>

          {/* Status */}
          <Field
            id="field-status"
            label="Status"
            required
            error={errors.status}
          >
            <div className={errors.status ? "border border-rose-500 rounded-xl" : ""}>
              <CustomCreatableDropdown
                options={statusOptions}
                value={
                  statusOptions.find(
                    (opt) => String(opt.value).toLowerCase() === String(status).toLowerCase()
                  ) || null
                }
                onChange={(val) => {
                  setStatus(val ? String(val.value).toLowerCase() : "");
                  if (errors.status && setErrors) {
                    setErrors((prev) => ({ ...prev, status: "" }));
                  }
                }}
                onCreateOption={handleCreateStatus}
                onEditOption={handleEditStatus}
                onDeleteOption={handleDeleteStatus}
                placeholder="Select or type status..."
                isClearable={false}
                isLoading={loadingStatus}
                menuHeader="Select Status"
              />
            </div>
          </Field>

          {/* Date Conducted */}
          <Field
            id="field-talkDate"
            label="Date Conducted"
            required
            error={errors.talkDate}
          >
            <div className={errors.talkDate ? "border border-rose-500 rounded-xl" : ""}>
              <DatePicker
                value={talkDate ? new Date(talkDate + "T00:00:00") : ""}
                onChange={([selectedDate]) => {
                  if (selectedDate) {
                    const y = selectedDate.getFullYear();
                    const m = String(selectedDate.getMonth() + 1).padStart(2, "0");
                    const d = String(selectedDate.getDate()).padStart(2, "0");
                    setTalkDate(`${y}-${m}-${d}`);
                  } else {
                    setTalkDate("");
                  }
                  if (errors.talkDate && setErrors) {
                    setErrors((prev) => ({ ...prev, talkDate: "" }));
                  }
                }}
                className="text-xs font-semibold"
                placeholder="Choose date"
                options={{
                  dateFormat: "d/m/Y",
                  disableMobile: true,
                }}
              />
            </div>
          </Field>

          {/* Time Conducted */}
          <Field
            id="field-time"
            label="Time Conducted"
            required
            error={errors.time}
          >
            <div className={errors.time ? "border border-rose-500 rounded-xl" : ""}>
              <TimePicker
                value={time}
                onChange={(e) => {
                  setTime(e.target.value);
                  if (errors.time && setErrors) {
                    setErrors((prev) => ({ ...prev, time: "" }));
                  }
                }}
              />
            </div>
          </Field>
        </div>

        {/* Topic */}
        <Field
          id="field-topic"
          label="Topic (brief briefing theme)"
          required
          error={errors.topic}
        >
          <input
            required
            type="text"
            value={topic}
            onChange={(e) => {
              setTopic(e.target.value);
              if (errors.topic && setErrors) {
                setErrors((prev) => ({ ...prev, topic: "" }));
              }
            }}
            placeholder="e.g. Working at Heights, Manual Handling, Silica Dust Precautions"
            className={`${INPUT_CLS} ${
              errors.topic
                ? "!border-rose-500 focus:!border-rose-500 focus:!ring-0 shadow-none"
                : ""
            }`}
            maxLength={225}
          />
          {!errors.topic && (
            <div className="mt-1 flex items-center justify-between px-1">
              <span className="text-[10px] text-slate-400">Max 225 characters</span>
              <span className="text-[10px] font-semibold text-slate-400">
                {topic?.length || 0}/225
              </span>
            </div>
          )}
        </Field>

        {/* Key Discussion Points */}
        <div id="field-discussionPoints" className="flex flex-col">
          <label className={LABEL_CLS}>
            <span>Key Discussion Points</span>
            <span className="text-rose-500 ml-0.5 font-bold">*</span>
          </label>
          <RichTextEditor
            value={discussionPoints}
            onChange={(val) => {
              setDiscussionPoints(val);
              if (errors.discussionPoints && setErrors) {
                setErrors((prev) => ({ ...prev, discussionPoints: "" }));
              }
            }}
            placeholder="Enter discussion points covered during the toolbox talk..."
            height="180px"
            maxLength={500}
            limitType="text"
            error={Boolean(errors.discussionPoints)}
            classNames={{
              wrapper: errors.discussionPoints ? "!border-rose-500 shadow-none" : "",
            }}
          />
          <div className="mt-1 flex items-center justify-between px-1">
            {errors.discussionPoints ? (
              <span className="text-[11px] font-semibold text-rose-500 animate-in fade-in duration-200">
                {errors.discussionPoints}
              </span>
            ) : (
              <span className="text-[10px] text-slate-400">
                Max 500 characters (excluding HTML formatting)
              </span>
            )}
            <span
              className={`text-[10px] font-semibold ${
                errors.discussionPoints ? "text-rose-500" : "text-slate-400"
              }`}
            >
              {stripHtml(discussionPoints).length}/500
            </span>
          </div>
        </div>

        {/* Attendees */}
        <div>
          <Field label="Attendees (Select Workers present)">
            <MultiSelectCheckboxDropdown
              options={users}
              value={selectedWorkers}
              onChange={setSelectedWorkers}
              placeholder={loadingJobUsers ? "Loading site workers..." : "Select workers present..."}
              allowCustom={false}
              usePortal={false}
            />
          </Field>
        </div>

        {/* Documents Upload */}
        <div>
          <label className={LABEL_CLS}>
            <span>Documents / Photo Evidence</span>
          </label>
          <AttachmentsActions
            files={docFiles}
            onChange={setDocFiles}
            addLabel="Upload Supporting Documents"
            showLabel={false}
            downloadKey={DocumentKeyEnum.ToolboxTalk}
            onPreviewFile={(file) => {
              setPreviewDoc(file);
              setIsPreviewOpen(true);
            }}
          />
        </div>

        {/* Notes */}
        <Field label="Notes">
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Enter notes, comments, or observation highlights..."
            className={`${INPUT_CLS} resize-none`}
            maxLength={2000}
          />
          <div className="mt-1 flex items-center justify-between px-1">
            <span className="text-[10px] text-slate-400">Max 2000 characters</span>
            <span className="text-[10px] font-semibold text-slate-400">
              {notes?.length || 0}/2000
            </span>
          </div>
        </Field>

        {/* Dynamic Custom Fields */}
        <EntityDynamicFieldsForm
          ref={dynamicFieldsRef}
          entityTypeCode={EntityTypeEnum.ToolboxTalks}
          companyId={companyId}
          userId={userId || localStorage.getItem("whs_user_id") || loggedInUser?.id}
        />
      </div>

      {/* Form Footer Action Buttons */}
      <div className="pt-3 border-t border-slate-200/70 flex gap-2.5">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1 py-2.5 sm:py-3 rounded-xl border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex-2 py-2.5 sm:py-3 px-4 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-bold text-xs shadow-md transition-all duration-200 disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer active:scale-[0.98]"
        >
          {isSubmitting ? (
            <>
              <ArrowPathIcon className="w-4 h-4 animate-spin text-white" />
              <span>Saving Toolbox Talk...</span>
            </>
          ) : (
            <span>Create Toolbox Talk</span>
          )}
        </button>
      </div>
    </form>
  );
}
